import { c as _c } from "react/compiler-runtime";
import CarouselComponent, { CarouselType } from '@spa/components/carousel/carousel.component';
import React, { ReactElement, useMemo } from 'react';
import { ButtonType } from '@spa/components/button/types/ButtonType';
import Link from 'next/link';
import LinkButtonComponent from '../button/link-button/link-button.component';
import { LinkTarget } from '@spa/lib/react/hooks/useLinkAttributes';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import assertNonUndefined from '@spa/lib/assertNonUndefined';
import classNames from 'clsx';
import { generatePaginatorPages } from './generate-paginator-pages';
import serializePath from '@spa/lib/router/serializePath';
import styles from './paginator.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import { usePathname } from 'next/navigation';
import useRuntimeSearchParams from '@spa/lib/react/hooks/useRuntimeSearchParams';
export interface Props {
  readonly id: string;
  readonly pageSize: number;
  readonly totalCount: number;
  readonly siblingCount: number;
  readonly currentPage: number;
  readonly paginationLimit?: number;
  readonly showButtonNavigation?: boolean;
}
function PaginatorComponent(t0) {
  const $ = _c(20);
  const {
    id,
    pageSize,
    totalCount,
    siblingCount,
    currentPage,
    paginationLimit,
    showButtonNavigation: t1
  } = t0;
  const showButtonNavigation = t1 === undefined ? true : t1;
  const {
    formatMessage
  } = useNamespacedIntl("paginator");
  const searchParams = useRuntimeSearchParams();
  const pathname = usePathname();
  const totalPages = Math.ceil(totalCount / pageSize);
  const effectiveTotalPages = paginationLimit !== undefined ? Math.min(totalPages, paginationLimit) : totalPages;
  let t2;
  if ($[0] !== currentPage || $[1] !== effectiveTotalPages || $[2] !== siblingCount) {
    t2 = generatePaginatorPages(siblingCount, currentPage, effectiveTotalPages);
    $[0] = currentPage;
    $[1] = effectiveTotalPages;
    $[2] = siblingCount;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const paginationRange = t2;
  let t3;
  if ($[4] !== pathname || $[5] !== searchParams) {
    t3 = page => {
      const newSearchParams = new URLSearchParams(searchParams);
      if (page === 1) {
        newSearchParams.delete("page");
      } else {
        newSearchParams.set("page", page.toString());
      }
      return serializePath(pathname, newSearchParams);
    };
    $[4] = pathname;
    $[5] = searchParams;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const getUrl = t3;
  let t4;
  if ($[7] !== paginationRange) {
    t4 = pageIndex => {
      const dotsIndex = paginationRange.indexOf(pageIndex);
      return pageIndex === -1 ? paginationRange[dotsIndex + 1] - 1 : paginationRange[dotsIndex - 1] + 1;
    };
    $[7] = paginationRange;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const getFirstHiddenPageIndex = t4;
  let t5;
  if ($[9] !== currentPage || $[10] !== effectiveTotalPages || $[11] !== formatMessage || $[12] !== getFirstHiddenPageIndex || $[13] !== getUrl || $[14] !== id || $[15] !== paginationRange || $[16] !== showButtonNavigation) {
    const generatePaginationElement = pageIndex_0 => {
      const shouldRenderDots = pageIndex_0 === -1 || pageIndex_0 === -2;
      const targetIndex = shouldRenderDots ? getFirstHiddenPageIndex(pageIndex_0) : pageIndex_0;
      return <li key={`paginator-${pageIndex_0}`} className={styles["paginator__item-wrapper"]}>{generatePaginationLink(targetIndex, shouldRenderDots)}</li>;
    };
    const generatePaginationLink = (pageIndex_1, t6) => {
      const shouldRenderDots_0 = t6 === undefined ? false : t6;
      return <Link prefetch={false} href={getUrl(pageIndex_1)} className={classNames(styles["paginator__item-link"], {
        [styles["paginator__item-link__dots"]]: shouldRenderDots_0
      })} aria-label={formatMessage("pageLink", {
        page: pageIndex_1
      })} data-testid={`pageLink-${pageIndex_1}`} key={`pageLink-${pageIndex_1}`}><span className={classNames([styles.paginator__item], {
          [styles["paginator__item--active"]]: currentPage === pageIndex_1
        })} data-testid={`pageIndex-${pageIndex_1}`}>{pageIndex_1}</span></Link>;
    };
    const navCarousel = () => {
      const navs = [];
      for (let navIndex = 0; navIndex < effectiveTotalPages; navIndex++) {
        navs.push(generatePaginationLink(navIndex + 1));
      }
      return <div className={styles["paginator__carousel-wrapper"]}><CarouselComponent id={`${id}-carousel`} carouselType={CarouselType.NAVIGATION} startIndex={currentPage - 1}>{navs}</CarouselComponent></div>;
    };
    t5 = paginationRange.length > 1 && <div className={styles.paginator}>{navCarousel()}<nav className={classNames(styles.paginator__indexes, {
        [styles["paginator__indexes--first-page"]]: currentPage === 1,
        [styles["paginator__indexes--last-page"]]: currentPage === paginationRange.at(-1)
      })} aria-label="Pagination"><ul className={classNames(styles.paginator__list, {
          [styles["paginator__list--long"]]: paginationRange.length >= 10
        })}>{paginationRange.map(generatePaginationElement)}</ul></nav>{showButtonNavigation && <div className={styles.paginator__buttons}>{currentPage > 1 && <div className={styles.paginator__button}><LinkButtonComponent link={{
            type: "internal",
            resolved: true,
            path: getUrl(currentPage - 1),
            linkTitle: formatMessage("prevpageLink"),
            linkParams: [],
            anchorTarget: null,
            target: LinkTarget.SELF
          }} data-testid="previous-link" aria-label={formatMessage("prevpageLink")} text={formatMessage("prevPage")} buttonType={ButtonType.SECONDARY} title={formatMessage("prevpageLink")} iconLeft={{
            width: 24,
            height: 24,
            src: "/assets/icons/arrow_line_back.svg",
            alt: formatMessage("prevPageArrow"),
            unoptimized: true
          }} /></div>}{currentPage < assertNonUndefined(paginationRange.at(-1)) && <div className={styles.paginator__button}><LinkButtonComponent link={{
            type: "internal",
            resolved: true,
            path: getUrl(currentPage + 1),
            linkTitle: formatMessage("nextpageLink"),
            linkParams: [],
            anchorTarget: null,
            target: LinkTarget.SELF
          }} data-testid="next-link" aria-label={formatMessage("nextpageLink")} text={currentPage === 1 ? formatMessage("nextPageSingle") : formatMessage("nextPage")} buttonType={ButtonType.SECONDARY} title={formatMessage("nextpageLink")} iconRight={{
            width: 24,
            height: 24,
            src: "/assets/icons/arrow_line_forward.svg",
            alt: formatMessage("nextPageArrow"),
            unoptimized: true
          }} /></div>}</div>}</div>;
    $[9] = currentPage;
    $[10] = effectiveTotalPages;
    $[11] = formatMessage;
    $[12] = getFirstHiddenPageIndex;
    $[13] = getUrl;
    $[14] = id;
    $[15] = paginationRange;
    $[16] = showButtonNavigation;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t5) {
    t6 = <>{t5}</>;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}
export default PaginatorComponent;